import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { KokeMascot } from '@/components/common/KokeMascot';
import LoadingTips from '@/components/common/LoadingTips';
import { isChunkLoadError, reloadForNewVersion, reloadNow } from '@/utils/chunkError';

/**
 * ページ描画中の例外を受け止める共通エラーバウンダリ。
 *
 * これが無いと、レンダー中の例外で React がツリー全体をアンマウントし、画面が真っ白になる。
 * 実際に起きていたのは次のケース:
 *
 *   1. リリースでデプロイされ、`assets/*.js` のファイル名が変わる
 *   2. デプロイ前から開きっぱなしのタブ（bfcache で戻った場合を含む）は古い index.html のまま
 *   3. そのままページ遷移すると、消えた旧チャンクを `React.lazy` が取りに行って失敗
 *   4. 例外がどこにも捕まらず白画面
 *
 * ここではチャンク読み込み失敗だけを特別扱いし、**1回だけ自動でリロード**して新しい
 * index.html を取り直す。それ以外の例外は、白画面ではなくエラー画面を出す。
 */

type Phase =
  /** 正常 */
  | 'ok'
  /** チャンク読み込み失敗 → 自動リロード中 */
  | 'reloading'
  /** チャンク読み込み失敗だが自動リロード済み（クールダウン中）→ 手動リロードを促す */
  | 'stale'
  /** その他の例外 */
  | 'error';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /**
   * この値が変わるとエラー状態を解除する（ページ遷移でのリセット用）。
   * children の再マウントを避けるため、key ではなく props で受ける。
   */
  resetKey?: string;
}

interface AppErrorBoundaryState {
  phase: Phase;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { phase: 'ok' };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    if (isChunkLoadError(error)) {
      // 実際のリロードは componentDidCatch で行う（描画中に副作用を起こさない）
      return { phase: 'reloading' };
    }
    return { phase: 'error' };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // 1回目は自動リロード。クールダウン中（＝リロードしても直らなかった）なら
      // 手動リロードの案内に切り替える。
      if (!reloadForNewVersion()) {
        this.setState({ phase: 'stale' });
      }
      return;
    }

    console.error('[AppErrorBoundary] 画面の描画に失敗しました', error, errorInfo.componentStack);
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    // 別のページに移ったらエラー表示を解除する（リロード待ちの間は保持）
    if (
      prevProps.resetKey !== this.props.resetKey &&
      (this.state.phase === 'error' || this.state.phase === 'stale')
    ) {
      this.setState({ phase: 'ok' });
    }
  }

  render(): ReactNode {
    const { phase } = this.state;

    if (phase === 'ok') return this.props.children;

    // リロードが走るまでの一瞬。白画面にせずローディング表示にする
    if (phase === 'reloading') return <LoadingTips className="bg-text-50" />;

    const isStale = phase === 'stale';

    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-text-50 px-4 text-center">
        <KokeMascot mood="apology" size="lg" className="mb-4" />
        <h1 className="text-xl sm:text-2xl font-semibold text-text-800 mb-2">
          {isStale ? '新しいバージョンが公開されています' : '画面の表示に失敗しました'}
        </h1>
        <p className="text-text-600 mb-6 max-w-md">
          {isStale
            ? 'ページを開いたままにしている間に更新がありました。読み込み直すと最新の画面になります。'
            : 'お手数ですが、ページを読み込み直してください。繰り返し発生する場合はお問い合わせください。'}
        </p>
        <button
          type="button"
          onClick={reloadNow}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
        >
          再読み込み
        </button>
      </div>
    );
  }
}

/**
 * ルーター配下で使うラッパー。ページ遷移でエラー表示をリセットする。
 */
export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <AppErrorBoundary resetKey={location.pathname}>{children}</AppErrorBoundary>;
}
